import { ColumnType } from "@/queries/smart-tables/types";

export interface SourceConfigurationShape {
  source?: string;
  sources?: string[];
  source_titles?: string[];
  source_types?: ColumnType[];
  pending_removed_sources?: string[];
  smart_table_id?: string;
  smart_table_title?: string;
  sheet_id?: string;
  sheet_title?: string;
}

export interface ChosenCompositionSource {
  resolver: string;
  smartTableId: string;
  smartTableTitle: string;
  sheetId: string;
  sheetTitle: string;
  columnId: string;
  columnTitle: string;
  columnType: ColumnType;
}

export const normalizeChosenSources = (
  chosen: ChosenCompositionSource | ChosenCompositionSource[],
): ChosenCompositionSource[] => {
  if (Array.isArray(chosen)) {
    return chosen;
  }
  return [chosen];
};

export const getActiveSourceResolvers = (configuration: SourceConfigurationShape): string[] => {
  if (configuration.sources && configuration.sources.length > 0) {
    return configuration.sources;
  }
  if (configuration.source) {
    return [configuration.source];
  }
  return [];
};

export const hasAnyConfiguredSources = (configuration: SourceConfigurationShape): boolean =>
  getActiveSourceResolvers(configuration).length > 0;

export const applyChosenSources = <T extends SourceConfigurationShape>(
  configuration: T,
  chosenInput: ChosenCompositionSource | ChosenCompositionSource[],
): T => {
  const chosen = normalizeChosenSources(chosenInput);
  if (chosen.length === 0) {
    return configuration;
  }

  const first = chosen[0];

  return {
    ...configuration,
    source: undefined,
    sources: chosen.map(item => item.resolver),
    source_titles: chosen.map(item => item.columnTitle),
    source_types: chosen.map(item => item.columnType),
    pending_removed_sources: undefined,
    smart_table_id: first.smartTableId,
    smart_table_title: first.smartTableTitle,
    sheet_id: first.sheetId,
    sheet_title: first.sheetTitle,
  };
};

export const clearConfiguredSources = <T extends SourceConfigurationShape>(
  configuration: T,
): T => ({
  ...configuration,
  source: undefined,
  sources: undefined,
  source_titles: undefined,
  source_types: undefined,
  pending_removed_sources: undefined,
});

export const removeConfiguredSourceAtIndex = <T extends SourceConfigurationShape>(
  configuration: T,
  indexToRemove: number,
): T => {
  const currentSources = getActiveSourceResolvers(configuration);
  if (indexToRemove < 0 || indexToRemove >= currentSources.length) {
    return configuration;
  }

  const removedSource = currentSources[indexToRemove];
  const pendingRemovedSources = configuration.pending_removed_sources ?? [];

  if (pendingRemovedSources.includes(removedSource)) {
    return configuration;
  }

  return {
    ...configuration,
    pending_removed_sources: [...pendingRemovedSources, removedSource],
  };
};

export const getSourceTitleAtIndex = (
  configuration: SourceConfigurationShape,
  index: number,
): string | undefined => configuration.source_titles?.[index];

export const getSourceTypeAtIndex = (
  configuration: SourceConfigurationShape,
  index: number,
): ColumnType | undefined => configuration.source_types?.[index];
